import { apiClient } from "./api-client-service";
import {
  AuthConfigSchema,
  AuthConfigUpdateSchema,
} from "./schemas/auth-config-schemas";

export interface AuthConfig {
  authconfig_id?: string;
  system_id: string;
  auth_type: "none" | "api_key" | "basic_auth" | "bearer_token" | "oauth2";
  api_key?: {
    name: string;
    value: string;
    location?: "header" | "query";
  };
  basic_auth?: {
    username: string;
    password: string;
  };
  bearer_token?: {
    token: string;
    header_name?: string;
  };
  oauth2?: {
    client_id: string;
    client_secret?: string;
    token_url: string;
    authorization_url?: string;
    scopes?: string[];
    grant_type?: string;
    auth_params?: Record<string, string>;
  };
  workspace_id?: string;
  organization_id?: string;
  created_time?: string;
  last_updated_time?: string;
  is_encrypted?: boolean;
}

export interface AuthConfigResponse {
  status: string;
  message: string;
  data: AuthConfig[];
  error_code?: string;
}

/**
 * Get authentication configuration for a system
 */
export async function getAuthConfig(
  systemId: string
): Promise<AuthConfigResponse> {
  const response = await apiClient.get<AuthConfigResponse>(
    `/api/ai-transform/v1/auth-config?system_id=${systemId}`
  );
  return response.data;
}

/**
 * Create authentication configuration for a system
 */
export async function createAuthConfig(
  authConfig: AuthConfig
): Promise<AuthConfigResponse> {
  const validatedAuthConfig = AuthConfigSchema.parse(authConfig);
  const response = await apiClient.post<AuthConfigResponse>(
    "/api/ai-transform/v1/auth-config",
    validatedAuthConfig
  );
  return response.data;
}

/**
 * Update authentication configuration for a system
 */
export async function updateAuthConfig(
  systemId: string,
  authConfig: Partial<AuthConfig>
): Promise<AuthConfigResponse> {
  const validatedAuthConfig = AuthConfigUpdateSchema.parse(authConfig);
  const response = await apiClient.put<AuthConfigResponse>(
    `/api/ai-transform/v1/auth-config?system_id=${systemId}`,
    validatedAuthConfig
  );
  return response.data;
}

/**
 * Delete authentication configuration for a system
 */
export async function deleteAuthConfig(
  systemId: string
): Promise<{ status: string; message: string }> {
  const response = await apiClient.delete<{ status: string; message: string }>(
    `/api/ai-transform/v1/auth-config?system_id=${systemId}`
  );
  return response.data;
}
